import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from 'sonner';
import { Save, Loader2, Info, Wallet, HelpCircle } from 'lucide-react';
import CustomPlansPanel from './pricing/CustomPlansPanel';
import OrgAllocationPanel from './pricing/OrgAllocationPanel';
import PricingFaqDrawer from './pricing/PricingFaqDrawer';
import AstridPricingDrawer from './pricing/AstridPricingDrawer';
import { AgentSparkleAvatar } from '../ai_agent/v2/AgentSparkleAvatar';

const NUM_FIELDS = [
  { key: 'usd_per_month', label: 'Price ($/mo)', step: '1' },
  { key: 'usd_per_year', label: 'Price ($/yr)', step: '1' },
  { key: 'bill_credits_per_month', label: 'Bill credits / mo', step: '1' },
  { key: 'ai_credits_grant_at_signup', label: 'AI credits (welcome)', step: '1' },
  { key: 'ai_credits_grant_per_month', label: 'AI credits / mo', step: '1' },
  { key: 'overage_per_bill_usd', label: 'Overage $/bill', step: '0.01' },
  { key: 'trial_days', label: 'Trial days', step: '1' },
];

function GlobalPlansPanel() {
  const [plans, setPlans] = useState([]);
  const [globalCfg, setGlobalCfg] = useState({ pay_per_bill_usd: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get('/super-admin/subscription/pricing');
      setPlans(data?.plans || []);
      const d = {};
      (data?.plans || []).forEach((p) => { d[p.id] = { ...p }; });
      setDrafts(d);
      setGlobalCfg({ pay_per_bill_usd: data?.pay_per_bill_usd ?? 1.39,
        credit_usd_value: data?.credit_usd_value ?? 0.10 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load pricing'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setField = (planId, key, value) => setDrafts((s) => ({ ...s, [planId]: { ...s[planId], [key]: value } }));

  const save = async (planId) => {
    setSaving(planId);
    try {
      const d = drafts[planId] || {};
      const patch = { visible: d.visible, label: d.label, tagline: d.tagline, cta_url: d.cta_url ?? null };
      NUM_FIELDS.forEach(({ key }) => {
        const v = d[key];
        patch[key] = (v === '' || v === null || v === undefined) ? null : Number(v);
      });
      const { data } = await api.put('/super-admin/subscription/pricing', { plan_id: planId, patch });
      setPlans(data?.plans || []);
      toast.success(`${drafts[planId]?.label || planId} saved`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(null); }
  };

  const saveGlobal = async () => {
    setSaving('__global__');
    try {
      await api.put('/super-admin/subscription/global-config', {
        pay_per_bill_usd: Number(globalCfg.pay_per_bill_usd),
      });
      toast.success('Global settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(null); }
  };

  if (loading) return <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div>
      <div className="rounded-none border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600 flex items-start gap-2 mb-5">
        <Info className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
        <span>Rename a plan by editing its <b>Name</b> and Save (id stays the same — Stripe/subscriptions safe). Leave a number blank for "unlimited/custom". Global plans always bill through Stripe. Toggle <b>Visible</b> off to hide from the picker.</span>
      </div>

      {/* Global settings */}
      <div className="bg-white border border-slate-200 mb-5" data-testid="pricing-global-settings">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 font-black tracking-tight text-sm" style={{ fontFamily: 'Chivo, sans-serif' }}>Global settings</div>
        <div className="p-5 flex flex-wrap items-end gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Pay-per-bill ($/bill, no subscription)</label>
            <Input type="number" step="0.01" value={globalCfg.pay_per_bill_usd ?? ''} onChange={(e) => setGlobalCfg((s) => ({ ...s, pay_per_bill_usd: e.target.value }))} className="rounded-none mt-1 font-mono text-sm w-48" data-testid="pricing-pay-per-bill" />
            <p className="text-[11px] text-slate-400 mt-1">Rate Free/Trial orgs pay to buy bill parsing without a plan.</p>
          </div>
          <Button onClick={saveGlobal} disabled={saving === '__global__'} className="rounded-none" size="sm" data-testid="pricing-global-save">
            {saving === '__global__' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Save settings
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {plans.map((p) => {
          const d = drafts[p.id] || {};
          return (
            <div key={p.id} className="bg-white border border-slate-200" data-testid={`sub-pricing-${p.id}`}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
                <div className="flex items-center gap-3">
                  <Input value={d.label ?? ''} onChange={(e) => setField(p.id, 'label', e.target.value)} className="rounded-none h-8 w-40 font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }} data-testid={`sub-pricing-label-${p.id}`} />
                  <Badge variant="outline" className="rounded-none text-slate-500 text-[10px]">{p.id}</Badge>
                  <Badge variant="outline" className="rounded-none text-[10px] text-sky-600 border-sky-300">global · Stripe</Badge>
                  {d.visible === false && <Badge variant="outline" className="rounded-none text-rose-600 text-[10px]">Hidden</Badge>}
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs text-slate-500">Visible
                    <Switch checked={d.visible !== false} onCheckedChange={(v) => setField(p.id, 'visible', v)} data-testid={`sub-pricing-visible-${p.id}`} />
                  </label>
                  <Button onClick={() => save(p.id)} disabled={saving === p.id} className="rounded-none" size="sm" data-testid={`sub-pricing-save-${p.id}`}>
                    {saving === p.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Save
                  </Button>
                </div>
              </div>
              <div className="p-5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {NUM_FIELDS.map(({ key, label, step }) => (
                  <div key={key}>
                    <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</label>
                    <Input type="number" step={step} value={d[key] ?? ''} onChange={(e) => setField(p.id, key, e.target.value)} className="rounded-none mt-1 font-mono text-sm" data-testid={`sub-pricing-${p.id}-${key}`} />
                    {key.startsWith('ai_credits_grant') && (
                      <p className="text-[10px] text-emerald-600 mt-1 font-mono" data-testid={`sub-pricing-${p.id}-${key}-usd`}>
                        = ${((Number(d[key]) || 0) * (globalCfg.credit_usd_value ?? 0.10)).toFixed(2)}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="px-5 pb-5">
                <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">External CTA URL (optional — opens instead of Stripe checkout, e.g. a contact-us page for sales-assisted plans)</label>
                <Input value={d.cta_url ?? ''} onChange={(e) => setField(p.id, 'cta_url', e.target.value)} placeholder="https://billportal.io/contact" className="rounded-none mt-1 font-mono text-sm" data-testid={`sub-pricing-cta-${p.id}`} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SubscriptionPricingTab() {
  const [faqOpen, setFaqOpen] = useState(false);
  const [astridOpen, setAstridOpen] = useState(false);

  return (
    <div className="max-w-6xl" data-testid="subscription-pricing-tab">
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-xl font-black tracking-tighter flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <Wallet className="h-5 w-5 text-primary" /> Subscription pricing
          </h2>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            Manage global plan tiers, create custom (managed-service) plans, and allocate plans to organizations.
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-none shrink-0" onClick={() => setFaqOpen(true)} data-testid="pricing-faq-btn">
          <HelpCircle className="h-4 w-4 mr-1" /> Setup FAQs
        </Button>
      </div>

      <Tabs defaultValue="global">
        <TabsList className="rounded-none">
          <TabsTrigger value="global" className="rounded-none" data-testid="pricing-tab-global">Global plans</TabsTrigger>
          <TabsTrigger value="custom" className="rounded-none" data-testid="pricing-tab-custom">Custom plans</TabsTrigger>
          <TabsTrigger value="alloc" className="rounded-none" data-testid="pricing-tab-alloc">Org allocation</TabsTrigger>
        </TabsList>
        <TabsContent value="global" className="mt-5"><GlobalPlansPanel /></TabsContent>
        <TabsContent value="custom" className="mt-5"><CustomPlansPanel /></TabsContent>
        <TabsContent value="alloc" className="mt-5"><OrgAllocationPanel /></TabsContent>
      </Tabs>

      <PricingFaqDrawer open={faqOpen} onOpenChange={setFaqOpen} />
      <AstridPricingDrawer open={astridOpen} onOpenChange={setAstridOpen} />

      {/* Floating Astrid launcher — standard bottom-right */}
      <button
        type="button"
        onClick={() => setAstridOpen(true)}
        data-testid="astrid-launcher"
        aria-label="Ask Astrid about pricing setup"
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-white shadow-lg border border-slate-200 pl-2 pr-4 py-2 hover:shadow-xl transition-shadow"
      >
        <AgentSparkleAvatar name="Astrid" size={30} pulse />
        <span className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'Chivo, sans-serif' }}>Ask Astrid</span>
      </button>
    </div>
  );
}
